import React, { useState, useMemo, useEffect, useRef } from "react";
import PT from "prop-types";
import { useForm, FormContext } from "react-hook-form";
import FormMessage from "_c/form/Message";
import FormSect from "_c/form/Sect";
import Select from "_c/Select";
import Checkbox from "_c/Checkbox";
import { getMinutesFromDate } from "@/utils/time";
import moment from "moment";
import { ServiceAPI } from "@/api/service";
import isequal from "lodash/isEqual";
import { serviceSchema } from "@/validation/service";
import { TextField, AbstractControlledField } from "_c/form/Field";
import { useNotify } from "@/hooks/useNotify";
import Fieldset from "_c/form/Fieldset";
import Calendar from "_c/Calendar";
import { useSelector } from "react-redux";
import { Selectors as UserSelectors } from "@/store/user/selectors";
import { Header, Content, FooterV2 } from "_c/Modals";
import NumberFormat from "react-number-format";
import TextInput from "_c/form/TextInput";
import SpecSelect from "./Spec";
import TitleSelect from "./Title";
import { MasterServicesService } from "@/services/masterServices";
import { useMut } from "@/hooks/useMut";
import { localStoragePersistor } from "@/utils/persistors";
import { useMasterId } from "@/hooks/useMasterId";

const ns = "common:service_editor";
const bottomId = "service-editor-bottom";
const genSpecPrefPersistKey = (masterId) =>
  `prefs:${masterId}:last_created_spec`;
const ServiceEditor = ({ service, t, ...props }) => {
  const authMasterId = useMasterId();
  const firstRender = useRef(true);
  const locale = useSelector(UserSelectors.getLocale);
  const currency = useSelector(UserSelectors.getCurrency);
  const { notifyError } = useNotify();
  const [units, setUnits] = useState([]);
  useEffect(() => {
    ServiceAPI.getUnits({ locale }).then(({ data }) =>
      setUnits(data.map(({ title }) => ({ label: title, value: title }))),
    );
  }, []);
  const formId = useMemo(() => {
    return `service-editor__${service ? service._id : "new"}`;
  }, [service]);
  const methods = useForm({
    validationSchema: serviceSchema,
    defaultValues: {
      disable_booking: service?.disable_booking,
      desc: service?.desc,
      spec:
        service?.spec ||
        props.defaultSpec ||
        localStoragePersistor.get(genSpecPrefPersistKey(props.masterId)) ||
        null,
      title: service?.title || props.defaultTitle || "",
      duration: service
        ? [moment().hour(0).minutes(service.duration).toDate()]
        : [moment().hour(0).minutes(30).toDate()],
      price: service ? service.price.amount : 1,
      per_unit: service ? service.per_unit : false,
      unit_name: service?.unit_name
        ? { value: service.unit_name, label: service.unit_name }
        : null,
    },
  });
  const {
    register,
    handleSubmit,
    errors,
    watch,
    setValue,
    setError,
    clearError,
  } = methods;
  const watchPerUnit = watch("per_unit");
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else if (watchPerUnit) {
      const $bottom = document.querySelector("#" + bottomId);
      if ($bottom) {
        setTimeout(() => {
          $bottom.scrollIntoView({
            behavior: "smooth",
          });
        }, 1);
      }
    }
  }, [watchPerUnit]);
  const watchSpec = watch("spec");
  const watchTitle = watch("title");
  const submitMut = useMut(
    async (form) => {
      clearError("server");
      const norm = {
        spec: form.spec,
        title: form.title,
        duration: getMinutesFromDate(moment(form.duration[0])),
        price: {
          currency,
          amount: form.price,
        },
        desc: form.desc,
        disable_booking: form.disable_booking,
        per_unit: form.per_unit,
        master: props.masterId,
        unit_name: form.per_unit ? form.unit_name.value : undefined,
      };
      if (service) {
        const updated = await MasterServicesService.updateService(
          service._id,
          norm,
        );
        return updated;
      } else {
        const newService = await MasterServicesService.createService(norm);
        return newService;
      }
    },
    {
      t,
      toast: () =>
        service ? t(`${ns}.submit_success_old`) : t(`${ns}.submit_success_new`),
      onSuccess(newOrUpdatedService) {
        if (service) {
          if (props.onUpdate) {
            props.onUpdate(newOrUpdatedService);
          }
        } else {
          if (props.onCreate) {
            props.onCreate(newOrUpdatedService);
          }
          localStoragePersistor.set(
            genSpecPrefPersistKey(props.masterId),
            newOrUpdatedService.spec,
          );
        }
        props.onClose();
      },
      notifyOnError: false,
      onError(e) {
        if (e === "MAX_SERVICES_WITHOUT_PREMIUM") {
          if (props.masterId === authMasterId) {
            props.openPremiumModal();
          } else {
            notifyError(t("errors:MAX_SERVICES_WITHOUT_PREMIUM"));
          }
        } else {
          notifyError(t([`errors:${e}`, "errors:unspecified_server"]));
          setError("server", "serverError", e);
        }
      },
    },
  );
  const onSubmit = (form) => submitMut.mutate(form);
  return (
    <>
      <Header onClick={props.onClose}>{t("common:close")}</Header>
      <FormContext {...methods}>
        <Content
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          id="service-editor-form"
        >
          <AbstractControlledField
            as={SpecSelect}
            t={t}
            ns={ns}
            isDisabled={Boolean(service)}
            name="spec"
            onChange={([spec]) => {
              if (!isequal(spec, watchSpec)) {
                if (watchTitle) {
                  setValue("title", null);
                }
              }
              return spec;
            }}
          />
          <AbstractControlledField
            t={t}
            ns={ns}
            name="title"
            as={TitleSelect}
            isDisabled={!watchSpec}
            allowCreation={!service}
            spec={watchSpec}
          />
          <TextField
            t={t}
            id={formId + "-desc"}
            name="desc"
            label={t(`${ns}.desc_label`)}
            autoComplete="off"
            maxLength="256"
            as="textarea"
            placeholder={t(`${ns}.desc_placeholder`)}
          />
          <AbstractControlledField
            label={t(`${ns}.duration_label`)}
            id={formId + "-dur"}
            name="duration"
            className="flatpickr-input"
            Component={Calendar}
            t={t}
            options={{
              disableMobile: true,
              dateFormat: "H:i",
              noCalendar: true,
              time_24hr: true,
              enableTime: true,
              minTime: "00:01",
              minuteIncrement: 1,
            }}
          />
          <AbstractControlledField
            Component={NumberFormat}
            customInput={TextInput}
            allowNegative={false}
            isNumericString={true}
            onChangeName="onValueChange"
            onChange={([{ floatValue }]) => floatValue}
            placeholder={t(`${ns}.price_placeholder`)}
            id={formId + "-price"}
            name="price"
            suffix={" " + currency}
            thousandSeparator
            t={t}
            label={t(`${ns}.price_label`)}
          />
          <FormSect>
            <Checkbox
              css={`
                strong {
                  color: var(--color-danger);
                }
              `}
              id={formId + "-disable_booking"}
              name="disable_booking"
              label={t(`${ns}.disable_booking`)}
              ref={register}
            />
          </FormSect>
          <Fieldset
            css={`
              border: none;
              padding: 0;
              margin: 0;
            `}
          >
            <FormSect>
              <Checkbox
                id={formId + "-per_unit"}
                name="per_unit"
                label={t(`${ns}.per_unit_label`)}
                ref={register}
              />
            </FormSect>
            {watchPerUnit && (
              <AbstractControlledField
                t={t}
                isSearchable={false}
                placeholder={""}
                label={t(`${ns}.unit_label`)}
                id={formId + "-unit"}
                noOptionsMessage={() => t("common:empty")}
                Component={Select}
                name="unit_name"
                options={units}
                sectStyle={{ paddingBottom: "100px" }}
              />
            )}
          </Fieldset>
          {errors.server && (
            <FormMessage
              error
              message={t([
                `errors:${errors.server.message}`,
                "errors:unspecified_server",
              ])}
            />
          )}
          <div
            id={bottomId}
            css={`
              height: 10px;
            `}
          />
        </Content>
      </FormContext>
      <FooterV2
        disabled={submitMut.isLoading}
        buttonType="submit"
        buttonForm="service-editor-form"
      >
        {service ? t("common:change") : t("common:add")}
      </FooterV2>
    </>
  );
};
ServiceEditor.propTypes = {
  onClose: PT.func.isRequired,
  service: PT.object,
  masterId: PT.string.isRequired,
  defaultSpec: PT.string,
  defaultTitle: PT.string,

  onCreate: PT.func,
  onUpdate: PT.func,
  openPremiumModal: PT.func.isRequired,
  t: PT.func.isRequired,
};

export default ServiceEditor;
